import { Component } from '@theme/component';

/** localStorage key holding the persisted accessibility settings object. */
const STORAGE_KEY = 'vpc-a11y-settings';

/**
 * Boolean toggle feature keys. Each maps 1:1 to an `a11y-<key>` class that is
 * added/removed on `document.documentElement` when the feature is on/off.
 * The visual effect of each class is implemented in CSS elsewhere — this
 * component only owns the toggling + persistence.
 *
 * @type {string[]}
 */
const BOOLEAN_FEATURES = [
  'text-spacing',
  'line-height',
  'align-left',
  'highlight-links',
  'high-contrast',
  'pause-animations',
  'dyslexia-font',
  'desaturate',
  'focus-outline',
];

/** The single range feature. Applied as the `--a11y-font-scale` custom property (e.g. `"125%"`) instead of a class. */
const RANGE_FEATURE = 'font-size';

/** id of the panel element (built on the theme's `<anchored-popover-component>` pattern). */
const PANEL_ID = 'accessibility-toolbar-panel';

/** id used to dedupe the lazily-injected dyslexia-font stylesheet <link>. */
const DYSLEXIA_FONT_LINK_ID = 'vpc-a11y-dyslexia-font-stylesheet';

/**
 * Google Fonts stylesheet URL for Lexend, used by the dyslexia-font feature.
 * Lexend (not a dyslexia-specific typeface like OpenDyslexic, whose real-world
 * benefit for dyslexic readers is genuinely mixed in the research) is backed
 * by reading-rate research and is guaranteed to actually load for every
 * visitor, since it's fetched from Google Fonts rather than merely named in
 * a font-family list and hoped for.
 */
const DYSLEXIA_FONT_STYLESHEET_URL = 'https://fonts.googleapis.com/css2?family=Lexend:wght@400;500;600;700&display=swap';

/**
 * Injects the Lexend stylesheet <link> into <head> the first time it's
 * needed, and never again. Called lazily (only once the dyslexia-font
 * feature is actually turned on) so visitors who never touch this toggle
 * never pay for the extra font request.
 */
function ensureDyslexiaFontLoaded() {
  if (document.getElementById(DYSLEXIA_FONT_LINK_ID)) return;

  const link = document.createElement('link');
  link.id = DYSLEXIA_FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href = DYSLEXIA_FONT_STYLESHEET_URL;
  document.head.appendChild(link);
}

/**
 * @typedef {Record<string, boolean | number>} AccessibilitySettings
 */

/**
 * @returns {AccessibilitySettings} A fresh copy of the default (all-off, 100%) settings.
 */
function getDefaultSettings() {
  return {
    'font-size': 100,
    'text-spacing': false,
    'line-height': false,
    'align-left': false,
    'highlight-links': false,
    'high-contrast': false,
    'pause-animations': false,
    'dyslexia-font': false,
    'desaturate': false,
    'focus-outline': false,
  };
}

/**
 * A toolbar of independent, persisted accessibility preferences (text spacing,
 * line height, alignment, link highlighting, contrast, animation pausing,
 * dyslexia-friendly font, desaturation, focus outlines, and font scaling).
 *
 * Each boolean feature toggles an `a11y-<key>` class on `<html>`. The single
 * range feature (`font-size`) sets the `--a11y-font-scale` custom property on
 * `<html>` instead. Settings persist to `localStorage` under `vpc-a11y-settings`
 * and are re-applied + re-synced to the controls on every connect.
 *
 * The open/close panel behavior is delegated to the native Popover API via the
 * theme's `<anchored-popover-component>` pattern (see assets/anchored-popover.js) —
 * this component does not open/close the panel itself, it only listens to the
 * panel's native `toggle` event to keep `aria-expanded` and focus restoration in
 * sync, since anchored-popover-component does not manage either of those itself.
 *
 * Dispatches {@link AccessibilityToolbarChangeEvent} on every settings change.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} trigger - The button that opens/closes the toolbar panel.
 * @property {HTMLElement} reset - The button that resets all settings to defaults.
 *
 * @extends {Component<Refs>}
 */
export class AccessibilityToolbar extends Component {
  requiredRefs = ['trigger', 'reset'];

  /** @type {AccessibilitySettings} */
  #settings = getDefaultSettings();

  /** @type {MediaQueryList} */
  #reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * @returns {HTMLElement | null} The panel element, looked up by id rather than
   * as a `ref` because it may live inside a nested `<anchored-popover-component>`
   * (a separate custom element boundary), which would otherwise claim the ref.
   */
  get #panel() {
    return document.getElementById(PANEL_ID);
  }

  connectedCallback() {
    super.connectedCallback();

    this.#settings = this.#loadSettings();
    this.#applyAll();
    this.#syncControls();

    this.addEventListener('click', this.#onClick);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('keydown', this.#onKeyDown);
    this.#reducedMotionQuery.addEventListener('change', this.#onReducedMotionChange);
    this.#panel?.addEventListener('toggle', this.#onPanelToggle);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.#reducedMotionQuery.removeEventListener('change', this.#onReducedMotionChange);
    this.#panel?.removeEventListener('toggle', this.#onPanelToggle);
  }

  /**
   * Reads + validates the persisted settings object.
   * Falls back to defaults if the key is missing, unparsable, or not an object.
   *
   * @returns {AccessibilitySettings}
   */
  #loadSettings() {
    const defaults = getDefaultSettings();

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return defaults;

      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return defaults;

      return { ...defaults, ...parsed };
    } catch (error) {
      return defaults;
    }
  }

  /**
   * Persists the current settings object to localStorage. Silently no-ops on
   * write failure (private browsing, storage quota, etc).
   */
  #persist() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.#settings));
    } catch (error) {
      // Ignore — accessibility effects still apply for this session, they just won't persist.
    }
  }

  /**
   * Applies every current setting to `document.documentElement`: one class per
   * boolean feature, plus the `--a11y-font-scale` custom property.
   */
  #applyAll() {
    for (const key of BOOLEAN_FEATURES) {
      this.#applyBooleanFeature(key, Boolean(this.#settings[key]));
    }
    document.documentElement.style.setProperty('--a11y-font-scale', `${this.#settings[RANGE_FEATURE]}%`);
  }

  /**
   * Adds/removes the `a11y-<key>` class on `<html>` for a single boolean feature.
   * `pause-animations` is additionally forced on when the OS-level
   * `prefers-reduced-motion: reduce` media query matches, without mutating the
   * user's stored preference for that feature. `dyslexia-font` additionally
   * lazy-loads the Lexend stylesheet the first time it's switched on.
   *
   * @param {string} key
   * @param {boolean} value
   */
  #applyBooleanFeature(key, value) {
    const forced = key === 'pause-animations' && this.#reducedMotionQuery.matches;
    const isOn = value || forced;

    document.documentElement.classList.toggle(`a11y-${key}`, isOn);

    if (key === 'dyslexia-font' && isOn) {
      ensureDyslexiaFontLoaded();
    }
  }

  /**
   * Syncs every `[data-feature]` control's UI state (`aria-pressed` for toggle
   * buttons, `.value` for the range input) to the current settings. Controls are
   * queried live via `querySelectorAll` rather than `refs`, since they may live
   * inside a nested custom element (e.g. the popover panel) whose boundary would
   * otherwise claim them as its own refs instead of this component's.
   */
  #syncControls() {
    for (const el of this.querySelectorAll('[data-feature]')) {
      const key = el.getAttribute('data-feature');
      if (!key) continue;

      if (el instanceof HTMLInputElement && key === RANGE_FEATURE) {
        el.value = String(this.#settings[RANGE_FEATURE] ?? 100);
      } else {
        el.setAttribute('aria-pressed', String(Boolean(this.#settings[key])));
      }
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (this.refs.reset.contains(target)) {
      this.#onReset();
      return;
    }

    const control = target.closest('[data-feature]');
    if (!(control instanceof HTMLButtonElement)) return;

    const key = control.getAttribute('data-feature');
    if (!key || key === RANGE_FEATURE) return;

    const next = !this.#settings[key];
    this.#settings[key] = next;
    control.setAttribute('aria-pressed', String(next));
    this.#applyBooleanFeature(key, next);
    this.#persist();
    this.#dispatchChange();
  };

  /** @param {Event} event */
  #onInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.getAttribute('data-feature') !== RANGE_FEATURE) return;

    const value = Number(target.value);
    if (Number.isNaN(value)) return;

    this.#settings[RANGE_FEATURE] = value;
    document.documentElement.style.setProperty('--a11y-font-scale', `${value}%`);
    this.#persist();
    this.#dispatchChange();
  };

  #onReset = () => {
    this.#settings = getDefaultSettings();

    for (const key of BOOLEAN_FEATURES) {
      // value is always `false` here; #applyBooleanFeature still re-forces the
      // pause-animations class on if the OS prefers reduced motion.
      this.#applyBooleanFeature(key, false);
    }
    document.documentElement.style.setProperty('--a11y-font-scale', `${this.#settings[RANGE_FEATURE]}%`);

    this.#syncControls();
    this.#persist();
    this.#dispatchChange();
  };

  #onReducedMotionChange = () => {
    this.#applyBooleanFeature('pause-animations', Boolean(this.#settings['pause-animations']));
  };

  /**
   * Escape-to-close safety net. The panel is a native Popover API element (per
   * the anchored-popover-component pattern), and browsers already dismiss an
   * "auto" popover on Escape on their own — so in the expected case this is a
   * no-op (`:popover-open` no longer matches by the time it would run). It only
   * does real work if the panel is ever built without native popover dismissal.
   * Either way, actual focus restoration happens in #onPanelToggle, from the
   * resulting native `toggle` event, so there's a single source of truth for it.
   *
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;

    const panel = this.#panel;
    if (panel instanceof HTMLElement && panel.matches(':popover-open')) {
      panel.hidePopover();
    }
  };

  /**
   * Keeps `aria-expanded` on the trigger in sync with the panel's native
   * open/closed state, and restores focus to the trigger when the panel closes
   * while focus was inside it (or lost) — covering Escape, light-dismiss (click
   * outside), and any other native close path uniformly.
   * `anchored-popover-component` (assets/anchored-popover.js) does not manage
   * either of these itself, so this component owns them.
   *
   * @param {ToggleEvent} event
   */
  #onPanelToggle = (event) => {
    const isOpen = event.newState === 'open';
    this.refs.trigger.setAttribute('aria-expanded', String(isOpen));

    if (!isOpen) {
      const active = document.activeElement;
      const panel = this.#panel;
      if (!active || active === document.body || (panel && panel.contains(active))) {
        this.refs.trigger.focus();
      }
    }
  };

  #dispatchChange() {
    this.dispatchEvent(new AccessibilityToolbarChangeEvent({ ...this.#settings }));
  }
}

if (!customElements.get('accessibility-toolbar-component')) {
  customElements.define('accessibility-toolbar-component', AccessibilityToolbar);
}

/**
 * Fired on `<accessibility-toolbar-component>` (bubbles) whenever any setting
 * changes — toggle click, range input, or reset — with the full current
 * settings object as `detail`.
 */
export class AccessibilityToolbarChangeEvent extends CustomEvent {
  static eventName = 'accessibility-toolbar:change';

  /** @param {AccessibilitySettings} detail */
  constructor(detail) {
    super(AccessibilityToolbarChangeEvent.eventName, { bubbles: true, detail });
  }
}
